import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faRefresh,
  faSearch,
  faPlay,
  faDownload,
  faPause,
  faStop,
  faPhone,
  faUserPlus,
  faBan,
  faCheck,
  faTags,
  faTrash,
  faPencil,
  faAddressBook,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { API_URL } from "../config";
import { showToast } from "../components/toast";
import LoadingSpinner from "../components/LoadingSpinner";
import AudioPlayerPopup from "../components/AudioPlayerPopup.jsx";
import AICampaignModal from "./AICampaignModal";
import "./subscribe.css";

const getFollowupContext = (callSummary, aiNotes) => {
  let summary = callSummary;
  if (typeof summary === "string") {
    try {
      summary = JSON.parse(summary);
    } catch {
      // Keep plain-text summaries as-is.
    }
  }

  if (summary && typeof summary === "object" && summary.call_summary) {
    summary = summary.call_summary;
  }

  if (summary && typeof summary === "object") {
    const overview = summary.call_overview || {};
    const structured = summary.structured_crm_fields || {};
    const handoff = summary.handoff_and_follow_up || {};
    summary =
      overview.overall_summary ||
      overview.one_line_summary ||
      summary.overall_summary ||
      summary.summary ||
      overview.call_outcome ||
      structured.calloutcome ||
      handoff.preferred_callback_time ||
      "";
  }

  const summaryText = typeof summary === "string" ? summary.trim() : "";
  const notesText = typeof aiNotes === "string" ? aiNotes.trim() : "";
  const lines = [];
  if (summaryText) lines.push(summaryText);
  if (notesText && notesText !== summaryText) lines.push(notesText);

  return {
    lines,
    text: lines.join("\n"),
  };
};

const AICallDashboard = () => {
  const [activeTab, setActiveTab] = useState("campaigns"); // 'campaigns' | 'customers' | 'logs'
  const [campaigns, setCampaigns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false);
  const [selectedCallForDetails, setSelectedCallForDetails] = useState(null);
  const [setupInfo, setSetupInfo] = useState(null);
  const [sarvamChecking, setSarvamChecking] = useState(false);
  const [syncingCampaignId, setSyncingCampaignId] = useState(null);
  const [popupAudio, setPopupAudio] = useState(null);

  // Live Campaign Details Modal State
  const [isCampaignDetailsModalOpen, setIsCampaignDetailsModalOpen] = useState(false);
  const [selectedCampaignDetails, setSelectedCampaignDetails] = useState(null);
  const [campaignDetailsLoading, setCampaignDetailsLoading] = useState(false);

  // Instant Single Call Modal State
  const [isInstantCallModalOpen, setIsInstantCallModalOpen] = useState(false);
  const [instantCallForm, setInstantCallForm] = useState({
    mobile: "",
    name: "",
    leadStatus: "new",
    subUid: "",
  });
  const [instantCallLoading, setInstantCallLoading] = useState(false);

  // Edit Campaign Modal State
  const [isEditCampaignModalOpen, setIsEditCampaignModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editCampaignForm, setEditCampaignForm] = useState({
    allowed_start_time: "09:00",
    allowed_end_time: "18:00",
    attempts_per_second: 0.1,
  });
  const [editCampaignLoading, setEditCampaignLoading] = useState(false);

  // Add Contacts to Campaign Modal State
  const [isAddContactsModalOpen, setIsAddContactsModalOpen] = useState(false);
  const [activeCampaignForContacts, setActiveCampaignForContacts] = useState(null);
  const [addContactsMobiles, setAddContactsMobiles] = useState("");
  const [addContactsLoading, setAddContactsLoading] = useState(false);

  // Add Customer Modal State
  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    name: "",
    mobile: "",
    lead_status: "new",
    dnd: false,
    details: "",
  });
  const [customerFormLoading, setCustomerFormLoading] = useState(false);

  // Custom Fields (Tag 2) State
  const [customFields, setCustomFields] = useState([]);
  const [isFieldsModalOpen, setIsFieldsModalOpen] = useState(false);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [newFieldForm, setNewFieldForm] = useState({
    entity: "customer",
    key: "",
    label: "",
    type: "string",
  });
  const [createFieldLoading, setCreateFieldLoading] = useState(false);

  // Writeback Queue (Tag 7) State
  const [writebackStatus, setWritebackStatus] = useState(null);
  const [writebackRetrying, setWritebackRetrying] = useState(false);

  // Table Header Filters
  // Campaigns
  const [campaignNameFilter, setCampaignNameFilter] = useState("");
  const [campaignSegmentFilter, setCampaignSegmentFilter] = useState("");
  const [campaignStatusFilter, setCampaignStatusFilter] = useState("");

  // Customers
  const [customerNameFilter, setCustomerNameFilter] = useState("");
  const [customerMobileFilter, setCustomerMobileFilter] = useState("");
  const [customerLeadStatusFilter, setCustomerLeadStatusFilter] = useState("");
  const [customerDndFilter, setCustomerDndFilter] = useState("");

  // Logs
  const [searchMobile, setSearchMobile] = useState("");
  const [searchSubUid, setSearchSubUid] = useState("");
  const [logStatusFilter, setLogStatusFilter] = useState("");
  const [logOutcomeFilter, setLogOutcomeFilter] = useState("");

  // Follow-ups Queue State
  const [followups, setFollowups] = useState([]);
  const [followupLoading, setFollowupLoading] = useState(false);
  const [followupCounts, setFollowupCounts] = useState({
    all: 0,
    scheduled: 0,
    queued: 0,
    in_progress: 0,
    completed: 0,
    failed: 0,
    cancelled: 0,
  });
  const [followupStatusFilter, setFollowupStatusFilter] = useState("all");
  const [followupDateFilter, setFollowupDateFilter] = useState("all");
  const [followupSearch, setFollowupSearch] = useState("");
  const [followupPage, setFollowupPage] = useState(1);
  const [followupTotal, setFollowupTotal] = useState(0);

  // Reschedule Modal State
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [reschedulingFollowup, setReschedulingFollowup] = useState(null);
  const [rescheduleDatetime, setRescheduleDatetime] = useState("");
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [actionFollowupId, setActionFollowupId] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem("authToken");
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  };

  const fetchCampaigns = async (showSpinner = true) => {
    try {
      if (showSpinner) setLoading(true);
      const res = await axios.get(
        `${API_URL}voiceCall/campaigns?limit=100`,
        getAuthHeader()
      );
      setCampaigns(res.data?.items || []);
    } catch (err) {
      console.error("Failed to fetch campaigns:", err);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  const fetchCustomers = async (search = "") => {
    try {
      setLoading(true);
      const queryMobile = search !== undefined && search !== null ? search : customerMobileFilter;
      const url = queryMobile
        ? `${API_URL}voiceCall/customers?mobile=${encodeURIComponent(queryMobile)}`
        : `${API_URL}voiceCall/customers`;
      const res = await axios.get(url, getAuthHeader());
      const list = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
      setCustomers(list);
    } catch (err) {
      console.error("Failed to fetch customers:", err);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async (customMobile, customSubUid) => {
    try {
      setLoading(true);
      const m = customMobile !== undefined ? customMobile : searchMobile;
      const s = customSubUid !== undefined ? customSubUid : searchSubUid;
      let url = `${API_URL}voiceCall/logs?limit=100`;
      if (m) url += `&mobile=${encodeURIComponent(m)}`;
      if (s) url += `&subUid=${encodeURIComponent(s)}`;

      const res = await axios.get(url, getAuthHeader());
      setLogs(res.data?.items || []);
    } catch (err) {
      console.error("Failed to fetch logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetCampaignFilters = () => {
    setCampaignNameFilter("");
    setCampaignSegmentFilter("");
    setCampaignStatusFilter("");
  };

  const handleResetCustomerFilters = () => {
    setCustomerNameFilter("");
    setCustomerMobileFilter("");
    setCustomerLeadStatusFilter("");
    setCustomerDndFilter("");
    fetchCustomers("");
  };

  const handleResetLogFilters = () => {
    setSearchMobile("");
    setSearchSubUid("");
    setLogStatusFilter("");
    setLogOutcomeFilter("");
    fetchLogs("", "");
  };

  const fetchFollowups = async (statusOverride, dateOverride, searchOverride, pageOverride) => {
    try {
      setFollowupLoading(true);
      const st = statusOverride !== undefined ? statusOverride : followupStatusFilter;
      const dt = dateOverride !== undefined ? dateOverride : followupDateFilter;
      const sr = searchOverride !== undefined ? searchOverride : followupSearch;
      const pg = pageOverride !== undefined ? pageOverride : followupPage;

      let url = `${API_URL}voiceCall/followups?page=${pg}&limit=25`;
      if (st && st !== "all") url += `&status=${encodeURIComponent(st)}`;
      if (dt && dt !== "all") url += `&dateFilter=${encodeURIComponent(dt)}`;
      if (sr && sr.trim()) url += `&search=${encodeURIComponent(sr.trim())}`;

      const res = await axios.get(url, getAuthHeader());
      setFollowups(res.data?.items || []);
      setFollowupTotal(res.data?.total || 0);
      if (res.data?.counts) {
        setFollowupCounts(res.data.counts);
      }
    } catch (err) {
      console.error("Failed to fetch followups:", err);
    } finally {
      setFollowupLoading(false);
    }
  };

  const handleTriggerFollowupNow = async (item) => {
    if (!window.confirm(`Are you sure you want to trigger an instant callback to ${item.customerName || item.mobile} now?`)) {
      return;
    }
    try {
      setActionFollowupId(item.id);
      const res = await axios.post(`${API_URL}voiceCall/followups/${item.id}/trigger`, {}, getAuthHeader());
      showToast.success(res.data?.message || "Follow-up call initiated successfully!");
      fetchFollowups();
    } catch (err) {
      console.error("Error triggering follow-up call:", err);
      showToast.error(err?.response?.data?.message || "Failed to trigger follow-up call");
    } finally {
      setActionFollowupId(null);
    }
  };

  const handleOpenRescheduleModal = (item) => {
    setReschedulingFollowup(item);
    if (item.scheduledTime) {
      const d = new Date(item.scheduledTime);
      const pad = (n) => String(n).padStart(2, "0");
      const localIso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      setRescheduleDatetime(localIso);
    } else {
      setRescheduleDatetime("");
    }
    setIsRescheduleModalOpen(true);
  };

  const handleSaveReschedule = async (e) => {
    if (e) e.preventDefault();
    if (!reschedulingFollowup || !rescheduleDatetime) {
      showToast.warning("Please select a valid date and time.");
      return;
    }
    try {
      setRescheduleLoading(true);
      await axios.patch(
        `${API_URL}voiceCall/followups/${reschedulingFollowup.id}/reschedule`,
        { scheduled_time: rescheduleDatetime },
        getAuthHeader()
      );
      showToast.success("Follow-up rescheduled successfully!");
      setIsRescheduleModalOpen(false);
      setReschedulingFollowup(null);
      fetchFollowups();
    } catch (err) {
      console.error("Error rescheduling follow-up:", err);
      showToast.error(err?.response?.data?.message || "Failed to reschedule follow-up");
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleCancelFollowup = async (item) => {
    if (!window.confirm(`Are you sure you want to cancel the scheduled callback for ${item.customerName || item.mobile}?`)) {
      return;
    }
    try {
      setActionFollowupId(item.id);
      await axios.patch(
        `${API_URL}voiceCall/followups/${item.id}/cancel`,
        { reason: "Cancelled by CRM user" },
        getAuthHeader()
      );
      showToast.success("Follow-up cancelled.");
      fetchFollowups();
    } catch (err) {
      console.error("Error cancelling follow-up:", err);
      showToast.error(err?.response?.data?.message || "Failed to cancel follow-up");
    } finally {
      setActionFollowupId(null);
    }
  };

  const handleResetFollowupFilters = () => {
    setFollowupStatusFilter("all");
    setFollowupDateFilter("all");
    setFollowupSearch("");
    setFollowupPage(1);
    fetchFollowups("all", "all", "", 1);
  };


  const fetchSetup = async () => {
    try {
      const res = await axios.get(`${API_URL}voiceCall/setup`, getAuthHeader());
      setSetupInfo(res.data);
    } catch (err) {
      console.error("Failed to fetch voice server setup:", err);
    }
  };

  const handleTestSarvam = async () => {
    try {
      setSarvamChecking(true);
      const res = await axios.post(
        `${API_URL}voiceCall/sarvam/check`,
        {},
        getAuthHeader()
      );
      showToast.success(
        res.data?.message ||
        `Sarvam check successful (${res.data?.status || "Connected"})`
      );
    } catch (err) {
      console.error("Sarvam check failed:", err);
      showToast.error(
        err?.response?.data?.message || "Sarvam provider check failed"
      );
    } finally {
      setSarvamChecking(false);
    }
  };

  const fetchWritebackStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}voiceCall/writeback`, getAuthHeader());
      setWritebackStatus(res.data);
    } catch (err) {
      console.warn("Could not fetch writeback queue status:", err);
    }
  };

  const handleRetryWriteback = async () => {
    try {
      setWritebackRetrying(true);
      const res = await axios.post(`${API_URL}voiceCall/writeback/retry`, {}, getAuthHeader());
      showToast.success(res.data?.message || `Requeued ${res.data?.requeued ?? 0} results for delivery`);
      fetchWritebackStatus();
    } catch (err) {
      console.error("Error retrying writeback:", err);
      showToast.error(err?.response?.data?.message || "Failed to retry writeback queue");
    } finally {
      setWritebackRetrying(false);
    }
  };

  useEffect(() => {
    fetchSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch single campaign details with live server sync
  const fetchCampaignDetail = async (id, showSpinner = false) => {
    try {
      if (showSpinner) setCampaignDetailsLoading(true);
      const res = await axios.get(`${API_URL}voiceCall/campaigns/${id}`, getAuthHeader());
      if (res.data) {
        setSelectedCampaignDetails(res.data);
      }
    } catch (err) {
      console.error("Error fetching campaign details:", err);
    } finally {
      if (showSpinner) setCampaignDetailsLoading(false);
    }
  };

  const handleOpenCampaignDetails = (campaign) => {
    setSelectedCampaignDetails(campaign);
    setIsCampaignDetailsModalOpen(true);
    fetchCampaignDetail(campaign.id, true);
  };

  useEffect(() => {
    let interval;
    if (activeTab === "campaigns") {
      fetchCampaigns(true);
      interval = setInterval(() => {
        fetchCampaigns(false);
      }, 60000); // Poll campaigns list every 1 minute
    } else if (activeTab === "customers") {
      fetchCustomers(customerMobileFilter);
    } else if (activeTab === "followups") {
      fetchFollowups();
      interval = setInterval(() => {
        fetchFollowups();
      }, 30000); // Poll followups queue every 30 seconds
    } else {
      fetchLogs();
      fetchWritebackStatus();
    }

    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Polling for campaign detail modal when open (every 1 minute)
  useEffect(() => {
    let detailInterval;
    if (isCampaignDetailsModalOpen && selectedCampaignDetails?.id) {
      detailInterval = setInterval(() => {
        fetchCampaignDetail(selectedCampaignDetails.id, false);
      }, 60000); // Poll active campaign details every 1 minute
    }
    return () => {
      if (detailInterval) clearInterval(detailInterval);
    };
  }, [isCampaignDetailsModalOpen, selectedCampaignDetails?.id]);

  const handleControlCampaign = async (id, action) => {
    try {
      await axios.post(
        `${API_URL}voiceCall/campaigns/${id}/${action}`,
        {},
        getAuthHeader()
      );
      showToast.success(`Campaign ${action}d successfully`);
      fetchCampaigns(false);
      if (isCampaignDetailsModalOpen && selectedCampaignDetails?.id === id) {
        fetchCampaignDetail(id, false);
      }
    } catch (err) {
      console.error(`Error ${action}ing campaign:`, err);
      showToast.error(
        err?.response?.data?.message || `Failed to ${action} campaign`
      );
    }
  };

  const handleDeleteCampaign = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete campaign "${name || id}"?`)) {
      return;
    }
    try {
      await axios.delete(
        `${API_URL}voiceCall/campaigns/${id}`,
        getAuthHeader()
      );
      showToast.success("Campaign deleted successfully");
      fetchCampaigns();
    } catch (err) {
      console.error("Error deleting campaign:", err);
      showToast.error(
        err?.response?.data?.message || "Failed to delete campaign"
      );
    }
  };

  const handleOpenEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setEditCampaignForm({
      allowed_start_time: campaign.allowed_start_time || "09:00",
      allowed_end_time: campaign.allowed_end_time || "18:00",
      attempts_per_second: campaign.attempts_per_second || 0.1,
    });
    setIsEditCampaignModalOpen(true);
  };

  const handleSaveEditCampaign = async (e) => {
    if (e) e.preventDefault();
    if (!editingCampaign) return;
    setEditCampaignLoading(true);
    try {
      await axios.patch(
        `${API_URL}voiceCall/campaigns/${editingCampaign.id}`,
        {
          allowed_start_time: editCampaignForm.allowed_start_time,
          allowed_end_time: editCampaignForm.allowed_end_time,
          attempts_per_second: Number(editCampaignForm.attempts_per_second) || 0.1,
        },
        getAuthHeader()
      );
      showToast.success("Campaign updated successfully");
      setIsEditCampaignModalOpen(false);
      setEditingCampaign(null);
      fetchCampaigns();
    } catch (err) {
      console.error("Error editing campaign:", err);
      showToast.error(
        err?.response?.data?.message || "Failed to edit campaign"
      );
    } finally {
      setEditCampaignLoading(false);
    }
  };

  const handleOpenAddContacts = (campaign) => {
    setActiveCampaignForContacts(campaign);
    setAddContactsMobiles("");
    setIsAddContactsModalOpen(true);
  };

  const handleSaveAddContacts = async (e) => {
    if (e) e.preventDefault();
    if (!activeCampaignForContacts) return;
    const rawMobiles = addContactsMobiles
      .split(/[\n,]+/)
      .map((m) => m.trim().replace(/\D/g, ""))
      .filter((m) => m.length >= 10);

    if (rawMobiles.length === 0) {
      showToast.warning("Please enter at least one valid mobile number (10 digits).");
      return;
    }

    setAddContactsLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}voiceCall/campaigns/${activeCampaignForContacts.id}/contacts`,
        {
          mobiles: rawMobiles.map((m) => `+91${m.slice(-10)}`),
        },
        getAuthHeader()
      );
      showToast.success(res?.data?.message || "Contacts added to campaign successfully!");
      setIsAddContactsModalOpen(false);
      setActiveCampaignForContacts(null);
      setAddContactsMobiles("");
      fetchCampaigns();
    } catch (err) {
      console.error("Error adding contacts to campaign:", err);
      showToast.error(
        err?.response?.data?.message || "Failed to add contacts to campaign"
      );
    } finally {
      setAddContactsLoading(false);
    }
  };

  const handleSyncCampaign = async (id) => {
    try {
      setSyncingCampaignId(id);
      await axios.post(
        `${API_URL}voiceCall/campaigns/${id}/sync`,
        {},
        getAuthHeader()
      );
      showToast.success("Campaign counts synced from server!");
      fetchCampaigns();
    } catch (err) {
      console.error("Error syncing campaign:", err);
      showToast.error(
        err?.response?.data?.message || "Failed to sync campaign counts"
      );
    } finally {
      setSyncingCampaignId(null);
    }
  };

  const handleTriggerInstantCall = async (e) => {
    if (e) e.preventDefault();
    if (!instantCallForm.mobile.trim()) {
      showToast.warning("Please enter a valid mobile number.");
      return;
    }

    try {
      setInstantCallLoading(true);
      const res = await axios.post(
        `${API_URL}voiceCall/instant`,
        instantCallForm,
        getAuthHeader()
      );
      showToast.success(res.data?.message || "AI Call placed successfully!");
      setIsInstantCallModalOpen(false);
      setInstantCallForm({ mobile: "", name: "", leadStatus: "new", subUid: "" });
      if (activeTab === "logs") fetchLogs();
      if (activeTab === "customers") fetchCustomers();
    } catch (err) {
      console.error("Error triggering instant call:", err);
      showToast.error(
        err?.response?.data?.message || "Failed to trigger AI call."
      );
    } finally {
      setInstantCallLoading(false);
    }
  };

  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    if (!customerForm.mobile.trim()) {
      showToast.warning("Please enter a mobile number.");
      return;
    }

    try {
      setCustomerFormLoading(true);
      await axios.post(
        `${API_URL}voiceCall/customers`,
        customerForm,
        getAuthHeader()
      );
      showToast.success("Customer saved on Voice Server!");
      setIsAddCustomerModalOpen(false);
      setCustomerForm({ name: "", mobile: "", lead_status: "new", dnd: false, details: "" });
      fetchCustomers();
    } catch (err) {
      console.error("Error saving customer:", err);
      showToast.error(err?.response?.data?.message || "Failed to save customer");
    } finally {
      setCustomerFormLoading(false);
    }
  };

  const handleToggleDnd = async (customer) => {
    const newDnd = !customer.dnd;
    try {
      await axios.post(
        `${API_URL}voiceCall/customers/${customer.mobile}/dnd`,
        { dnd: newDnd },
        getAuthHeader()
      );
      showToast.success(`DND ${newDnd ? "enabled" : "disabled"} for ${customer.mobile}`);
      fetchCustomers(customerMobileFilter);
    } catch (err) {
      console.error("Error updating DND:", err);
      showToast.error("Failed to update DND status");
    }
  };

  const handleDirectCallCustomer = async (mobile, name = "") => {
    setInstantCallForm({
      mobile,
      name,
      leadStatus: "contacted",
      subUid: "",
    });
    setIsInstantCallModalOpen(true);
  };

  const fetchCustomFields = async () => {
    try {
      setFieldsLoading(true);
      const res = await axios.get(`${API_URL}voiceCall/fields`, getAuthHeader());
      const list = Array.isArray(res.data) ? res.data : [];
      setCustomFields(list);
    } catch (err) {
      console.error("Failed to fetch custom fields:", err);
      setCustomFields([]);
    } finally {
      setFieldsLoading(false);
    }
  };

  const handleCreateField = async (e) => {
    e.preventDefault();
    if (!newFieldForm.key.trim() || !newFieldForm.label.trim()) {
      showToast.warning("Please provide both field key and label.");
      return;
    }

    try {
      setCreateFieldLoading(true);
      await axios.post(
        `${API_URL}voiceCall/fields`,
        {
          entity: newFieldForm.entity,
          key: newFieldForm.key.trim().toLowerCase().replace(/\s+/g, "_"),
          label: newFieldForm.label.trim(),
          type: newFieldForm.type,
        },
        getAuthHeader()
      );
      showToast.success("Custom field created successfully!");
      setNewFieldForm({ entity: "customer", key: "", label: "", type: "string" });
      fetchCustomFields();
    } catch (err) {
      console.error("Error creating custom field:", err);
      showToast.error(
        err?.response?.data?.message || err?.response?.data?.detail || "Failed to create custom field"
      );
    } finally {
      setCreateFieldLoading(false);
    }
  };

  // Metric stats
  const totalCampaigns = campaigns.length;
  const activeCampaigns = campaigns.filter(
    (c) => c.status === "running" || c.status === "launching"
  ).length;
  const totalReached = campaigns.reduce(
    (acc, c) => acc + (Number(c.reachedCount) || 0),
    0
  );

  // Filtered lists for tables
  const filteredCampaigns = campaigns.filter((c) => {
    if (
      campaignNameFilter &&
      !c.campaignName?.toLowerCase().includes(campaignNameFilter.toLowerCase())
    ) {
      return false;
    }
    if (
      campaignSegmentFilter &&
      !c.segment?.toLowerCase().includes(campaignSegmentFilter.toLowerCase())
    ) {
      return false;
    }
    if (
      campaignStatusFilter &&
      c.status?.toLowerCase() !== campaignStatusFilter.toLowerCase()
    ) {
      return false;
    }
    return true;
  });

  const filteredCustomers = customers.filter((c) => {
    if (
      customerNameFilter &&
      !c.name?.toLowerCase().includes(customerNameFilter.toLowerCase())
    ) {
      return false;
    }
    if (
      customerMobileFilter &&
      !String(c.mobile || "").toLowerCase().includes(customerMobileFilter.toLowerCase())
    ) {
      return false;
    }
    if (
      customerLeadStatusFilter &&
      c.lead_status?.toLowerCase() !== customerLeadStatusFilter.toLowerCase()
    ) {
      return false;
    }
    if (customerDndFilter !== "") {
      const isDnd = customerDndFilter === "true";
      if (Boolean(c.dnd) !== isDnd) return false;
    }
    return true;
  });

  const filteredLogs = logs.filter((log) => {
    if (
      logStatusFilter &&
      log.status?.toLowerCase() !== logStatusFilter.toLowerCase()
    ) {
      return false;
    }
    if (
      logOutcomeFilter &&
      log.outcome?.toLowerCase() !== logOutcomeFilter.toLowerCase()
    ) {
      return false;
    }
    return true;
  });

  return (
    <div className="panel-content mt-5 panel-content-responsive">
      {loading && <LoadingSpinner />}

      <style>{`
        .ai-voice-table thead tr:first-child th {
          position: sticky;
          top: 0;
          z-index: 10;
          background: #f8f9fa !important;
          border-bottom: 1px solid #dee2e6 !important;
          border-top: none !important;
          color: #343a40 !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          white-space: nowrap;
          padding: 8px 10px !important;
        }
        .ai-voice-table thead tr:nth-child(2) th {
          position: sticky;
          top: 34px;
          z-index: 9;
          background: #ffffff !important;
          border-bottom: 2px solid #dee2e6 !important;
          border-top: none !important;
          padding: 4px 6px !important;
          vertical-align: middle;
        }
        .ai-voice-table thead tr:nth-child(2) th input,
        .ai-voice-table thead tr:nth-child(2) th select {
          width: 100%;
          height: 28px;
          font-size: 11px;
          border: 1px solid #ced4da;
          border-radius: 3px;
          background-color: #fff;
          font-weight: normal;
          padding: 3px 6px;
        }
        .ai-voice-table tbody td {
          font-size: 12px !important;
          vertical-align: middle !important;
          padding: 8px 10px !important;
          border-top: 1px solid #edf2f7 !important;
          border-bottom: none !important;
          color: #212529;
        }
        .ai-voice-table tbody tr:hover td {
          background-color: #f8fafc !important;
        }
        .ai-voice-table-container {
          max-height: calc(100vh - 280px);
          overflow: auto;
        }
      `}</style>

      {/* Top Header */}
      <div className="panel-heading d-flex justify-content-between align-items-center mb-3">
        <div>
          <div className="d-flex align-items-center">
            <h4 style={{ margin: 0, fontWeight: "bold" }}>AI Voice Calling & Campaigns</h4>
            {setupInfo && (
              <span
                className="badge bg-light text-dark border ms-2"
                style={{ fontSize: "11px", padding: "4px 8px", fontWeight: "normal" }}
                title={`Connection: ${setupInfo.lines?.[0]?.connection_id || "default"}`}
              >
                Provider: <strong className="text-uppercase">{setupInfo.provider || "Sarvam"}</strong>
                {setupInfo.lines?.[0]?.phone_numbers?.length ? ` (${setupInfo.lines[0].phone_numbers[0]})` : ""}
              </span>
            )}
          </div>
          <span className="text-muted" style={{ fontSize: "0.85rem" }}>
            Manage outbound AI voice campaigns, customers, single calls, and recordings
          </span>
        </div>

        <div className="d-flex align-items-center" style={{ gap: "6px" }}>
          <button
            type="button"
            className="btn btn-default btn-sm"
            onClick={handleTestSarvam}
            disabled={sarvamChecking}
            title="Check Sarvam Provider API Connection"
          >
            {sarvamChecking ? "Testing..." : "Test API Connection"}
          </button>
          <button
            type="button"
            className="btn btn-default btn-sm"
            onClick={() => {
              if (activeTab === "campaigns") fetchCampaigns();
              else if (activeTab === "customers") fetchCustomers(customerMobileFilter);
              else if (activeTab === "followups") fetchFollowups();
              else fetchLogs();
            }}
          >
            <FontAwesomeIcon icon={faRefresh} /> Refresh
          </button>
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={() => setIsInstantCallModalOpen(true)}
          >
            <FontAwesomeIcon icon={faPhone} /> Instant Single Call
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setIsLaunchModalOpen(true)}
          >
            <FontAwesomeIcon icon={faPlus} /> Launch Campaign
          </button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="row mb-3">
        <div className="col-md-4">
          <div className="panel panel-default with-rounded-corner with-shadow p-3 mb-0">
            <div className="text-muted" style={{ fontSize: "12px", textTransform: "uppercase", fontWeight: 600 }}>
              Total Campaigns
            </div>
            <h3 className="m-0 mt-1 font-weight-bold">{totalCampaigns}</h3>
          </div>
        </div>

        <div className="col-md-4">
          <div className="panel panel-default with-rounded-corner with-shadow p-3 mb-0">
            <div className="text-muted" style={{ fontSize: "12px", textTransform: "uppercase", fontWeight: 600 }}>
              Active / Running
            </div>
            <h3 className="m-0 mt-1 font-weight-bold text-success">{activeCampaigns}</h3>
          </div>
        </div>

        <div className="col-md-4">
          <div className="panel panel-default with-rounded-corner with-shadow p-3 mb-0">
            <div className="text-muted" style={{ fontSize: "12px", textTransform: "uppercase", fontWeight: 600 }}>
              Total Leads Reached
            </div>
            <h3 className="m-0 mt-1 font-weight-bold text-primary">{totalReached}</h3>
          </div>
        </div>
      </div>

      {/* Nav Tabs */}
      <ul className="nav nav-tabs mb-3" id="nav-tabs">
        <li className={`nav-item ${activeTab === "campaigns" ? "active" : ""}`}>
          <a
            href="#campaigns"
            className={`nav-link ${activeTab === "campaigns" ? "active font-weight-bold" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              setActiveTab("campaigns");
            }}
            style={{
              cursor: "pointer",
              color: activeTab === "campaigns" ? "#007bff" : "#495057",
            }}
          >
            Campaigns List
          </a>
        </li>
        <li className={`nav-item ${activeTab === "customers" ? "active" : ""}`}>
          <a
            href="#customers"
            className={`nav-link ${activeTab === "customers" ? "active font-weight-bold" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              setActiveTab("customers");
            }}
            style={{
              cursor: "pointer",
              color: activeTab === "customers" ? "#007bff" : "#495057",
            }}
          >
            Customers & Single Call
          </a>
        </li>
        <li className={`nav-item ${activeTab === "followups" ? "active" : ""}`}>
          <a
            href="#followups"
            className={`nav-link ${activeTab === "followups" ? "active font-weight-bold" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              setActiveTab("followups");
            }}
            style={{
              cursor: "pointer",
              color: activeTab === "followups" ? "#007bff" : "#495057",
            }}
          >
            Follow-up Calls Queue
            {followupCounts?.scheduled > 0 && (
              <span
                className="badge bg-warning text-dark ms-2"
                style={{ fontSize: "10px", padding: "2px 6px", borderRadius: "10px" }}
              >
                {followupCounts.scheduled}
              </span>
            )}
          </a>
        </li>
        <li className={`nav-item ${activeTab === "logs" ? "active" : ""}`}>
          <a
            href="#logs"
            className={`nav-link ${activeTab === "logs" ? "active font-weight-bold" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              setActiveTab("logs");
            }}
            style={{
              cursor: "pointer",
              color: activeTab === "logs" ? "#007bff" : "#495057",
            }}
          >
            Call Logs & Recordings
          </a>
        </li>
      </ul>

      {/* Tab Content: Campaigns */}
      {activeTab === "campaigns" && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="table-responsive ai-voice-table-container">
            <table className="table table-hover table-condensed bg-white m-b-0 ai-voice-table sticky-table">
              <thead>
                <tr className="bg-light">
                  <th style={{ width: "52%", minWidth: "320px" }}>Campaign Details</th>
                  <th style={{ width: "33%", minWidth: "220px" }}>Progress</th>
                  <th style={{ width: "15%", minWidth: "150px", textAlign: "right" }}>Actions</th>
                </tr>
                <tr>
                  <th>
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: "6px" }}>
                      <input
                        type="text"
                        placeholder="Search Name..."
                        value={campaignNameFilter}
                        onChange={(e) => setCampaignNameFilter(e.target.value)}
                        style={{ maxWidth: "160px" }}
                      />
                      <input
                        type="text"
                        placeholder="Search Segment..."
                        value={campaignSegmentFilter}
                        onChange={(e) => setCampaignSegmentFilter(e.target.value)}
                        style={{ maxWidth: "140px" }}
                      />
                      <select
                        value={campaignStatusFilter}
                        onChange={(e) => setCampaignStatusFilter(e.target.value)}
                        style={{ maxWidth: "130px" }}
                      >
                        <option value="">All Statuses</option>
                        <option value="running">Running</option>
                        <option value="completed">Completed</option>
                        <option value="paused">Paused</option>
                        <option value="launching">Launching</option>
                        <option value="ended">Ended</option>
                        <option value="canceled">Canceled</option>
                        <option value="failed">Failed</option>
                      </select>
                    </div>
                  </th>
                  <th></th>
                  <th style={{ textAlign: "right" }}>
                    {(campaignNameFilter || campaignSegmentFilter || campaignStatusFilter) && (
                      <button
                        type="button"
                        className="btn btn-default btn-xs"
                        onClick={handleResetCampaignFilters}
                        title="Reset Filters"
                      >
                        Reset
                      </button>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center text-muted p-4">
                      {campaigns.length === 0
                        ? 'No AI voice campaigns created yet. Click "Launch Campaign" to start.'
                        : "No campaigns matching the filter criteria."}
                    </td>
                  </tr>
                ) : (
                  filteredCampaigns.map((c) => {
                    const isCallingFinished = Boolean(c.callingFinished || c.calling_finished);

                    const totalLeads = Number(c.totalLeads) || 0;
                    const reachedCount = Number(c.reachedCount) || 0;
                    const retryCount = Number(c.retryCount || c.counts?.retry || 0);
                    const skippedCount = Number(c.skippedCount) || 0;
                    const failedCount = isCallingFinished
                      ? Math.max(0, totalLeads - reachedCount - skippedCount)
                      : Number(c.failedCount || c.counts?.failed || 0);

                    const reachedPct = totalLeads > 0 ? (reachedCount / totalLeads) * 100 : 0;
                    const retryPct = totalLeads > 0 ? (retryCount / totalLeads) * 100 : 0;
                    const failedPct = totalLeads > 0 ? (failedCount / totalLeads) * 100 : 0;
                    const remainingCount = Math.max(0, totalLeads - reachedCount - retryCount - failedCount - skippedCount);

                    const versionLabel = c.app_version || c.version || 9;
                    const attemptsRate = c.attempts_per_second || 0.1;
                    const startTime = c.allowed_start_time || "09:30";
                    const endTime = c.allowed_end_time || "19:00";

                    let badgeClass = "badge badge-secondary bg-secondary text-white";
                    let badgeStyle = {};
                    let statusDisplay = c.status ? c.status.charAt(0).toUpperCase() + c.status.slice(1) : "Unknown";

                    if (isCallingFinished) {
                      badgeClass = "badge text-white";
                      badgeStyle = { backgroundColor: "#10b981" };
                      statusDisplay = (
                        <>
                          <FontAwesomeIcon icon={faCheck} style={{ fontSize: "10px" }} /> All calls done
                        </>
                      );
                    } else if (retryCount > 0 && (c.status === "running" || c.status === "active")) {
                      badgeClass = "badge badge-warning text-dark";
                      statusDisplay = (
                        <>
                          <span
                            style={{
                              display: "inline-block",
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              backgroundColor: "#d97706",
                              marginRight: "4px",
                            }}
                          />
                          Active ({retryCount} will retry)
                        </>
                      );
                    } else if (c.status === "running" || c.status === "active") {
                      badgeClass = "badge text-white";
                      badgeStyle = { backgroundColor: "#10b981" };
                      statusDisplay = (
                        <>
                          <span
                            style={{
                              display: "inline-block",
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              backgroundColor: "#fff",
                              marginRight: "4px",
                            }}
                          />
                          Active
                        </>
                      );
                    } else if (c.status === "completed" || c.status === "ended") {
                      badgeClass = "badge text-white";
                      badgeStyle = { backgroundColor: "#10b981" };
                      statusDisplay = "Completed";
                    } else if (c.status === "paused") {
                      badgeClass = "badge badge-warning bg-warning text-dark";
                      statusDisplay = "Paused";
                    } else if (c.status === "failed") {
                      badgeClass = "badge badge-danger bg-danger text-white";
                      statusDisplay = "Failed";
                    }

                    return (
                      <tr key={c.id}>
                        {/* Column 1: Campaign Details */}
                        <td style={{ verticalAlign: "middle", padding: "12px 14px" }}>
                          <div className="d-flex flex-column" style={{ gap: "4px" }}>
                            {/* Campaign Title */}
                            <div>
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleOpenCampaignDetails(c);
                                }}
                                className="text-decoration-none"
                                style={{
                                  fontWeight: "600",
                                  fontSize: "14.5px",
                                  color: "#1e293b",
                                  borderBottom: "1px solid #cbd5e1",
                                  paddingBottom: "1px",
                                  display: "inline-block",
                                }}
                                title="Click to view live campaign details"
                              >
                                {c.campaignName}
                              </a>
                            </div>

                            {/* Status Badge + Customers Count */}
                            <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px", fontSize: "12px" }}>
                              <span
                                className={badgeClass}
                                style={{
                                  padding: "3px 8px",
                                  fontSize: "11px",
                                  fontWeight: "500",
                                  borderRadius: "4px",
                                  textTransform: "capitalize",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  ...badgeStyle,
                                }}
                              >
                                {statusDisplay}
                              </span>

                              <span style={{ color: "#64748b", fontWeight: "400" }}>
                                {totalLeads} customers
                              </span>
                            </div>

                            {/* Subtext: version | speed | calls timing */}
                            <div
                              className="d-flex align-items-center flex-wrap"
                              style={{ gap: "10px", fontSize: "11.5px", color: "#64748b" }}
                            >
                              <span>version {versionLabel}</span>
                              <span>{attemptsRate}/s</span>
                              <span>calls {startTime}–{endTime}</span>
                              {c.segment && (
                                <span className="text-muted" title="Segment">
                                  • {c.segment}
                                </span>
                              )}
                              {c.createdOn && (
                                <span className="text-muted" title="Created On">
                                  • {new Date(c.createdOn).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Column 2: Progress */}
                        <td style={{ verticalAlign: "middle", padding: "12px 14px" }}>
                          <div style={{ width: "100%", maxWidth: "420px" }}>
                            {/* Sleek Multi-color Segmented Progress Bar */}
                            <div
                              role="progressbar"
                              aria-label={`${reachedCount} reached, ${retryCount} retry, ${failedCount} failed of ${totalLeads}`}
                              title={`${reachedCount} reached, ${retryCount} will retry, ${failedCount} failed (${totalLeads} total)`}
                              style={{
                                width: "100%",
                                height: "8px",
                                backgroundColor: "#e2e8f0",
                                borderRadius: "9999px",
                                overflow: "hidden",
                                marginBottom: "6px",
                                display: "flex",
                              }}
                            >
                              {/* Reached (Green) */}
                              {reachedPct > 0 && (
                                <div
                                  style={{
                                    width: `${reachedPct}%`,
                                    height: "100%",
                                    backgroundColor: "#10b981",
                                    transition: "width 0.4s ease",
                                  }}
                                />
                              )}
                              {/* Will Retry (Yellow / Amber) */}
                              {retryPct > 0 && (
                                <div
                                  style={{
                                    width: `${retryPct}%`,
                                    height: "100%",
                                    backgroundColor: "#f59e0b",
                                    transition: "width 0.4s ease",
                                  }}
                                />
                              )}
                              {/* Failed / Unreached (Red) */}
                              {failedPct > 0 && (
                                <div
                                  style={{
                                    width: `${failedPct}%`,
                                    height: "100%",
                                    backgroundColor: "#ef4444",
                                    transition: "width 0.4s ease",
                                  }}
                                />
                              )}
                            </div>

                            {/* Multi-indicator labels */}
                            <div
                              className="d-flex align-items-center flex-wrap"
                              style={{ fontSize: "12px", color: "#64748b", gap: "10px" }}
                            >
                              {/* Reached count */}
                              {reachedCount > 0 && (
                                <span className="d-inline-flex align-items-center" style={{ fontWeight: "600", color: "#10b981" }}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: "7px",
                                      height: "7px",
                                      backgroundColor: "#10b981",
                                      borderRadius: "1.5px",
                                      marginRight: "5px",
                                    }}
                                  />
                                  {reachedCount} reached
                                </span>
                              )}

                              {/* Retry count (Yellow) */}
                              {retryCount > 0 && (
                                <span className="d-inline-flex align-items-center" style={{ fontWeight: "600", color: "#d97706" }}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: "7px",
                                      height: "7px",
                                      backgroundColor: "#f59e0b",
                                      borderRadius: "1.5px",
                                      marginRight: "5px",
                                    }}
                                  />
                                  {retryCount} will retry
                                </span>
                              )}

                              {/* Failed count (Red) */}
                              {failedCount > 0 && (
                                <span className="d-inline-flex align-items-center" style={{ fontWeight: "600", color: "#ef4444" }}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: "7px",
                                      height: "7px",
                                      backgroundColor: "#ef4444",
                                      borderRadius: "1.5px",
                                      marginRight: "5px",
                                    }}
                                  />
                                  {failedCount} {isCallingFinished ? "unreached" : "failed"}
                                </span>
                              )}

                              {/* All queued / untouched */}
                              {reachedCount === 0 && retryCount === 0 && failedCount === 0 && (
                                <span className="d-inline-flex align-items-center text-muted" style={{ fontSize: "11.5px" }}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: "7px",
                                      height: "7px",
                                      backgroundColor: "#94a3b8",
                                      borderRadius: "1.5px",
                                      marginRight: "5px",
                                    }}
                                  />
                                  {totalLeads} queued
                                </span>
                              )}

                              {/* Remaining untouched when some calls made */}
                              {remainingCount > 0 && (reachedCount > 0 || retryCount > 0 || failedCount > 0) && (
                                <span className="text-muted" style={{ fontSize: "11px" }}>
                                  / {remainingCount} remaining
                                </span>
                              )}

                              {/* Skipped */}
                              {skippedCount > 0 && (
                                <span className="text-muted" style={{ fontSize: "11px" }}>
                                  ({skippedCount} skipped)
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Column 3: Actions */}
                        <td style={{ verticalAlign: "middle", padding: "12px 14px", textAlign: "right" }}>
                          <div className="d-flex gap-1 align-items-center justify-content-end">
                            <button
                              type="button"
                              className="btn btn-default btn-xs mx-1"
                              onClick={() => handleSyncCampaign(c.id)}
                              disabled={syncingCampaignId === c.id}
                              title="Sync latest live counts from Voice Server"
                            >
                              <FontAwesomeIcon icon={faRefresh} spin={syncingCampaignId === c.id} />
                            </button>

                            {/* When calling is finished: only Close (/stop) button */}
                            {isCallingFinished ? (
                              <>
                                {c.status !== "ended" && c.status !== "canceled" && (
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-xs mx-1"
                                    onClick={() => handleControlCampaign(c.id, "stop")}
                                    title="Close Campaign"
                                  >
                                    <FontAwesomeIcon icon={faStop} /> Close
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                {c.status === "running" && (
                                  <button
                                    type="button"
                                    className="btn btn-warning btn-xs mx-1"
                                    onClick={() => handleControlCampaign(c.id, "pause")}
                                    title="Pause Campaign"
                                  >
                                    <FontAwesomeIcon icon={faPause} />
                                  </button>
                                )}
                                {c.status === "paused" && (
                                  <button
                                    type="button"
                                    className="btn btn-success btn-xs mx-1"
                                    onClick={() => handleControlCampaign(c.id, "resume")}
                                    title="Resume Campaign"
                                  >
                                    <FontAwesomeIcon icon={faPlay} />
                                  </button>
                                )}
                                {c.status !== "ended" && c.status !== "canceled" && c.status !== "completed" && (
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-xs mx-1"
                                    onClick={() => handleControlCampaign(c.id, "stop")}
                                    title="Stop Campaign"
                                  >
                                    <FontAwesomeIcon icon={faStop} />
                                  </button>
                                )}
                                {c.status !== "ended" && c.status !== "canceled" && c.status !== "completed" && (
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-xs mx-1"
                                    onClick={() => handleOpenAddContacts(c)}
                                    title="Add Contacts to Campaign"
                                  >
                                    <FontAwesomeIcon icon={faAddressBook} />
                                  </button>
                                )}
                              </>
                            )}

                            <button
                              type="button"
                              className="btn btn-warning btn-xs mx-1"
                              onClick={() => handleOpenEditCampaign(c)}
                              title="Edit Campaign Settings"
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </button>

                            {/* Eye button at the LAST of actions */}
                            <button
                              type="button"
                              className="btn btn-info btn-xs mx-1 text-white"
                              onClick={() => handleOpenCampaignDetails(c)}
                              title="View Live Campaign Status & Contacts"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab Content: Customers & Single Call */}
      {activeTab === "customers" && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          {/* Action buttons bar without search inputs */}
          <div className="p-2 px-3 bg-light border-bottom d-flex align-items-center justify-content-end" style={{ gap: "6px" }}>
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={() => {
                fetchCustomFields();
                setIsFieldsModalOpen(true);
              }}
            >
              <FontAwesomeIcon icon={faTags} /> Custom Fields
            </button>
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={() => setIsAddCustomerModalOpen(true)}
            >
              <FontAwesomeIcon icon={faUserPlus} /> Add Customer
            </button>
          </div>

          <div className="table-responsive ai-voice-table-container">
            <table className="table table-hover table-condensed bg-white m-b-0 ai-voice-table sticky-table">
              <thead>
                <tr className="bg-light">
                  <th style={{ minWidth: "160px" }}>Name</th>
                  <th style={{ minWidth: "180px" }}>Mobile</th>
                  <th style={{ minWidth: "140px" }}>Lead Status</th>
                  <th style={{ minWidth: "140px" }}>DND (Do Not Call)</th>
                  <th style={{ minWidth: "110px" }}>Created</th>
                  <th style={{ minWidth: "110px" }}>Actions</th>
                </tr>
                <tr>
                  <th>
                    <input
                      type="text"
                      placeholder="Filter Name..."
                      value={customerNameFilter}
                      onChange={(e) => setCustomerNameFilter(e.target.value)}
                    />
                  </th>
                  <th>
                    <div className="d-flex align-items-center gap-1">
                      <input
                        type="text"
                        placeholder="Search Mobile..."
                        value={customerMobileFilter}
                        onChange={(e) => setCustomerMobileFilter(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") fetchCustomers(customerMobileFilter);
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-default btn-xs btn-group-xs"
                        onClick={() => fetchCustomers(customerMobileFilter)}
                        title="Search Mobile on server"
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </th>
                  <th>
                    <select
                      value={customerLeadStatusFilter}
                      onChange={(e) => setCustomerLeadStatusFilter(e.target.value)}
                    >
                      <option value="">--select--</option>
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="interested">Interested</option>
                      <option value="follow_up">Follow Up</option>
                      <option value="not_interested">Not Interested</option>
                    </select>
                  </th>
                  <th>
                    <select
                      value={customerDndFilter}
                      onChange={(e) => setCustomerDndFilter(e.target.value)}
                    >
                      <option value="">--select--</option>
                      <option value="true">DND: ON</option>
                      <option value="false">DND: OFF</option>
                    </select>
                  </th>
                  <th></th>
                  <th>
                    <div className="d-flex align-items-center gap-1">
                      <button
                        type="button"
                        className="btn btn-default btn-xs btn-group-xs"
                        onClick={() => fetchCustomers(customerMobileFilter)}
                        title="Search"
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted p-4">
                      {customers.length === 0
                        ? 'No customers found on Voice Server. Click "Add Customer" or "Instant Single Call" to dial someone.'
                        : "No customers matching the filter criteria."}
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((c) => (
                    <tr key={c.id || c.mobile}>
                      <td className="font-weight-bold">{c.name || "Customer"}</td>
                      <td>{c.mobile || "-"}</td>
                      <td>
                        <span className="badge badge-secondary text-capitalize" style={{ padding: "4px 8px" }}>
                          {c.lead_status || "new"}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`btn btn-xs ${c.dnd ? "btn-danger" : "btn-default"}`}
                          onClick={() => handleToggleDnd(c)}
                          title="Click to toggle DND"
                        >
                          <FontAwesomeIcon icon={c.dnd ? faBan : faCheck} /> {c.dnd ? "DND: ON" : "DND: OFF"}
                        </button>
                      </td>
                      <td className="text-muted small">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString() : "-"}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-success btn-xs mx-1"
                          onClick={() => handleDirectCallCustomer(c.mobile, c.name)}
                          disabled={c.dnd}
                          title={c.dnd ? "Customer has opted out (DND)" : "Dial call now"}
                        >
                          <FontAwesomeIcon icon={faPhone} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab Content: Call Logs */}
      {activeTab === "logs" && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          {/* Delivery Queue Status (Tag 7) without search bar */}
          {writebackStatus && (
            <div className="p-2 px-3 bg-light border-bottom d-flex align-items-center justify-content-end gap-2 flex-wrap">
              <span
                className="badge bg-light text-dark border align-middle"
                style={{ fontSize: "11px", padding: "5px 8px" }}
                title="Results Delivery Queue between Voice Server and CRM"
              >
                Delivery Queue:{" "}
                <strong className={writebackStatus.queue?.failed > 0 ? "text-danger" : "text-success"}>
                  {writebackStatus.queue?.failed > 0
                    ? `${writebackStatus.queue.failed} Failed`
                    : "Healthy"}
                </strong>
                {writebackStatus.queue?.pending > 0 ? ` (${writebackStatus.queue.pending} pending)` : ""}
                {writebackStatus.queue?.sent ? ` · ${writebackStatus.queue.sent} delivered` : ""}
              </span>
              {writebackStatus?.queue?.failed > 0 && (
                <button
                  type="button"
                  className="btn btn-warning btn-sm"
                  onClick={handleRetryWriteback}
                  disabled={writebackRetrying}
                  title="Requeue failed write-backs"
                >
                  {writebackRetrying ? "Retrying..." : "Retry Failed Deliveries"}
                </button>
              )}
            </div>
          )}

          <div className="table-responsive ai-voice-table-container">
            <table className="table table-hover table-condensed bg-white m-b-0 ai-voice-table sticky-table">
              <thead>
                <tr className="bg-light">
                  <th style={{ minWidth: "140px" }}>Time</th>
                  <th style={{ minWidth: "120px" }}>Sub UID</th>
                  <th style={{ minWidth: "140px" }}>Mobile</th>
                  <th style={{ minWidth: "120px" }}>Status</th>
                  <th style={{ minWidth: "90px" }}>Duration</th>
                  <th style={{ minWidth: "130px" }}>Disposition</th>
                  <th style={{ minWidth: "110px" }} className="text-center">Recording</th>
                  <th style={{ minWidth: "100px" }}>Transcript</th>
                  <th style={{ minWidth: "80px" }}>Actions</th>
                </tr>
                <tr>
                  <th></th>
                  <th>
                    <input
                      type="text"
                      placeholder="Sub UID"
                      value={searchSubUid}
                      onChange={(e) => setSearchSubUid(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") fetchLogs();
                      }}
                    />
                  </th>
                  <th>
                    <div className="d-flex align-items-center gap-1">
                      <input
                        type="text"
                        placeholder="Mobile"
                        value={searchMobile}
                        onChange={(e) => setSearchMobile(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") fetchLogs();
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-default btn-xs btn-group-xs"
                        onClick={() => fetchLogs()}
                        title="Search"
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </th>
                  <th>
                    <select
                      value={logStatusFilter}
                      onChange={(e) => setLogStatusFilter(e.target.value)}
                    >
                      <option value="">--select--</option>
                      <option value="completed">Completed</option>
                      <option value="in-progress">In Progress</option>
                      <option value="ringing">Ringing</option>
                      <option value="failed">Failed</option>
                      <option value="busy">Busy</option>
                      <option value="no-answer">No Answer</option>
                    </select>
                  </th>
                  <th></th>
                  <th>
                    <select
                      value={logOutcomeFilter}
                      onChange={(e) => setLogOutcomeFilter(e.target.value)}
                    >
                      <option value="">--select--</option>
                      <option value="interested">Interested</option>
                      <option value="not_interested">Not Interested</option>
                      <option value="follow_up">Follow Up</option>
                      <option value="callback">Callback</option>
                      <option value="connected">Connected</option>
                      <option value="busy">Busy</option>
                      <option value="no_answer">No Answer</option>
                    </select>
                  </th>
                  <th></th>
                  <th></th>
                  <th>
                    <div className="d-flex align-items-center gap-1">
                      <button
                        type="button"
                        className="btn btn-default btn-xs btn-group-xs"
                        onClick={() => fetchLogs()}
                        title="Search"
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center text-muted p-4">
                      {logs.length === 0
                        ? "No call logs found."
                        : "No call logs matching the filter criteria."}
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => (
                    <tr key={log.id}>
                      <td className="text-muted small">
                        {log.createdOn ? new Date(log.createdOn).toLocaleString() : "-"}
                      </td>
                      <td className="font-weight-bold text-primary">
                        {log.subUid || "-"}
                      </td>
                      <td className="font-weight-bold">
                        {log.mobile || "-"}
                      </td>
                      <td>
                        <span
                          className={`badge ${log.status === "completed" ? "badge-success" : "badge-secondary"}`}
                          style={{ padding: "4px 8px" }}
                        >
                          {log.status}
                        </span>
                      </td>
                      <td className="text-muted">
                        {log.durationSeconds ? `${log.durationSeconds}s` : "0s"}
                      </td>
                      <td className="font-weight-bold text-primary">
                        {log.outcome || "N/A"}
                      </td>
                      <td className="text-center">
                        {log.recordingUrl ? (
                          <>
                            <a
                              href="#"
                              className="play_audio text-primary"
                              onClick={(e) => {
                                e.preventDefault();
                                setPopupAudio({
                                  name: log.mobile ? `Call with ${log.mobile}` : `Call Log #${log.id}`,
                                  audioUrl: log.recordingUrl,
                                });
                              }}
                              title="Play Recording"
                            >
                              <FontAwesomeIcon icon={faPlay} />
                            </a>
                            <a
                              href={log.recordingUrl}
                              className="btn btn-sm text-primary"
                              style={{ marginLeft: "10px" }}
                              download={`recording_voice_${log.id || "call"}.mp3`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Download Recording"
                            >
                              <FontAwesomeIcon icon={faDownload} />
                            </a>
                          </>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </td>
                      <td>
                        {log.transcript ? (
                          <button
                            type="button"
                            className="btn btn-default btn-xs"
                            onClick={() => setSelectedCallForDetails(log)}
                          >
                            View ({Array.isArray(log.transcript) ? log.transcript.length : 1} turns)
                          </button>
                        ) : (
                          <span className="text-muted small">None</span>
                        )}
                      </td>
                      <td></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab Content: Follow-up Queue */}
      {activeTab === "followups" && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          {/* Filter Bar */}
          <div className="p-3 bg-light border-bottom d-flex flex-wrap align-items-center" style={{ gap: "8px" }}>
            <div className="d-flex flex-wrap align-items-center" style={{ gap: "8px" }}>
              <select
                className="form-control form-control-sm"
                style={{ width: "140px" }}
                value={followupStatusFilter}
                onChange={(e) => {
                  setFollowupStatusFilter(e.target.value);
                  fetchFollowups(e.target.value, followupDateFilter, followupSearch, 1);
                }}
              >
                <option value="all">All Statuses</option>
                <option value="scheduled">Scheduled</option>
                <option value="queued">Queued</option>
                <option value="in_progress">Dialing Now</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>

              <select
                className="form-control form-control-sm"
                style={{ width: "140px" }}
                value={followupDateFilter}
                onChange={(e) => {
                  setFollowupDateFilter(e.target.value);
                  fetchFollowups(followupStatusFilter, e.target.value, followupSearch, 1);
                }}
              >
                <option value="all">All Dates</option>
                <option value="today">Today's Follow-ups</option>
                <option value="upcoming">Upcoming Only</option>
              </select>

              <div className="input-group input-group-sm" style={{ width: "200px" }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search mobile, name..."
                  value={followupSearch}
                  onChange={(e) => setFollowupSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      fetchFollowups(followupStatusFilter, followupDateFilter, followupSearch, 1);
                    }
                  }}
                />
                <button
                  className="btn btn-default"
                  type="button"
                  onClick={() => fetchFollowups(followupStatusFilter, followupDateFilter, followupSearch, 1)}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </button>
              </div>

              <button
                type="button"
                className="btn btn-default btn-sm"
                onClick={handleResetFollowupFilters}
                title="Reset Filters"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Follow-ups Table */}
          <div className="table-responsive ai-voice-table-container">
            <table className="table table-hover table-condensed bg-white m-b-0 ai-voice-table sticky-table">
              <thead>
                <tr className="bg-light">
                  <th style={{ minWidth: "160px" }}>Customer</th>
                  <th style={{ minWidth: "130px" }}>Mobile</th>
                  <th style={{ minWidth: "170px" }}>Scheduled Callback Time</th>
                  <th style={{ minWidth: "130px" }}>Queue Status</th>
                  <th style={{ minWidth: "220px" }}>Context / Notes</th>
                  <th style={{ minWidth: "140px" }}>Call Result</th>
                  <th style={{ minWidth: "180px", textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {followupLoading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <LoadingSpinner />
                      <div className="text-muted mt-2" style={{ fontSize: "12px" }}>
                        Loading scheduled follow-up calls...
                      </div>
                    </td>
                  </tr>
                ) : followups.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      <div style={{ fontSize: "14px", fontWeight: "bold" }}>No follow-up calls found</div>
                      <div className="small mt-1 text-muted">
                        Calls scheduled by AI voice agent or CRM will appear here in the real-time queue.
                      </div>
                    </td>
                  </tr>
                ) : (
                  followups.map((item) => {
                    const followupContext = getFollowupContext(item.callSummary, item.aiNotes);
                    const isDue =
                      item.status === "scheduled" &&
                      new Date(item.scheduledTime).getTime() <= Date.now();

                    const schedDate = new Date(item.scheduledTime);
                    const isToday =
                      !isNaN(schedDate.getTime()) &&
                      schedDate.toDateString() === new Date().toDateString();

                    const timeFormatted = !isNaN(schedDate.getTime())
                      ? schedDate.toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : "-";

                    const dateFormatted = !isNaN(schedDate.getTime())
                      ? schedDate.toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "-";

                    return (
                      <tr key={item.id} className={isDue ? "table-warning" : ""}>
                        <td>
                          <div className="font-weight-bold text-dark">
                            {item.customerName || "Valued Customer"}
                          </div>
                          {item.subUid && (
                            <span className="text-muted small" style={{ fontSize: "11px" }}>
                              UID: {item.subUid}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className="font-monospace" style={{ fontSize: "12px" }}>
                            {item.mobile}
                          </span>
                        </td>
                        <td>
                          <div>
                            {isToday ? (
                              <span className="badge bg-primary text-white me-1" style={{ fontSize: "10px" }}>
                                TODAY
                              </span>
                            ) : (
                              <span className="text-dark small me-1">{dateFormatted}</span>
                            )}
                            <strong className="text-dark">{timeFormatted}</strong>
                          </div>
                          {isDue && (
                            <span className="text-danger small font-weight-bold" style={{ fontSize: "10px" }}>
                              ● Due for instant dial
                            </span>
                          )}
                        </td>
                        <td>
                          {item.status === "scheduled" && (
                            <span className="badge bg-primary text-white" style={{ fontSize: "11px", padding: "4px 8px" }}>
                              Scheduled
                            </span>
                          )}
                          {item.status === "queued" && (
                            <span className="badge bg-warning text-dark" style={{ fontSize: "11px", padding: "4px 8px" }}>
                              Queued
                            </span>
                          )}
                          {item.status === "in_progress" && (
                            <span className="badge bg-info text-white" style={{ fontSize: "11px", padding: "4px 8px" }}>
                              Dialing...
                            </span>
                          )}
                          {item.status === "completed" && (
                            <span className="badge bg-success text-white" style={{ fontSize: "11px", padding: "4px 8px" }}>
                              Completed
                            </span>
                          )}
                          {item.status === "failed" && (
                            <span
                              className="badge bg-danger text-white"
                              style={{ fontSize: "11px", padding: "4px 8px", cursor: "help" }}
                              title={item.failureReason || "Call failed"}
                            >
                              Failed
                            </span>
                          )}
                          {item.status === "cancelled" && (
                            <span
                              className="badge bg-secondary text-white"
                              style={{ fontSize: "11px", padding: "4px 8px", cursor: "help" }}
                              title={item.failureReason || "Cancelled"}
                            >
                              Cancelled
                            </span>
                          )}
                        </td>
                        <td>
                          {followupContext.lines.length > 0 ? (
                            <div
                              className="text-muted"
                              style={{
                                maxWidth: "210px",
                                fontSize: "10px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={followupContext.text}
                            >
                              {followupContext.text}
                            </div>
                          ) : (
                            <span className="text-muted small">Scheduled callback</span>
                          )}
                          {item.failureReason && item.status === "failed" && (
                            <div className="text-danger small mt-1" style={{ fontSize: "10px" }}>
                              Err: {item.failureReason}
                            </div>
                          )}
                        </td>
                        <td>
                          {item.callDuration ? (
                            <span className="small text-dark">
                              {item.callDuration}s {item.callOutcome ? `(${item.callOutcome})` : ""}
                            </span>
                          ) : item.status === "completed" ? (
                            <span className="small text-success">Finished</span>
                          ) : (
                            <span className="text-muted small">-</span>
                          )}
                          {item.recordingUrl && (
                            <button
                              type="button"
                              className="btn btn-default btn-xs ms-2"
                              onClick={() =>
                                setPopupAudio({
                                  name: `Followup Call - ${item.customerName || item.mobile}`,
                                  audioUrl: item.recordingUrl,
                                })
                              }
                              title="Listen Call Recording"
                            >
                              <FontAwesomeIcon icon={faPlay} className="text-success" />
                            </button>
                          )}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <div className="btn-group btn-group-xs">
                            {/* Call Now button */}
                            {item.status !== "completed" && item.status !== "in_progress" && (
                              <button
                                type="button"
                                className="btn btn-success btn-xs"
                                disabled={actionFollowupId === item.id}
                                onClick={() => handleTriggerFollowupNow(item)}
                                title="Dial this customer right now"
                              >
                                <FontAwesomeIcon icon={faPhone} className="me-1" />
                                {actionFollowupId === item.id ? "Calling..." : "Call Now"}
                              </button>
                            )}

                            {/* Reschedule button */}
                            {item.status !== "completed" && (
                              <button
                                type="button"
                                className="btn btn-primary btn-xs"
                                onClick={() => handleOpenRescheduleModal(item)}
                                title="Change callback date/time"
                              >
                                <FontAwesomeIcon icon={faPencil} className="me-1" /> Reschedule
                              </button>
                            )}

                            {/* Cancel button */}
                            {item.status === "scheduled" && (
                              <button
                                type="button"
                                className="btn btn-danger btn-xs"
                                onClick={() => handleCancelFollowup(item)}
                                title="Cancel callback"
                              >
                                <FontAwesomeIcon icon={faBan} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {isRescheduleModalOpen && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
            onClick={() => setIsRescheduleModalOpen(false)}
          >
            <div className="modal-dialog modal-sm" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title font-weight-bold" style={{ fontSize: "14px" }}>
                    Reschedule Follow-up Call
                  </h5>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setIsRescheduleModalOpen(false)}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <form onSubmit={handleSaveReschedule}>
                  <div className="modal-body">
                    <div className="mb-2">
                      <label className="form-label small text-muted">Customer</label>
                      <div className="font-weight-bold text-dark">
                        {reschedulingFollowup?.customerName || "Valued Customer"} ({reschedulingFollowup?.mobile})
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label small text-muted">
                        New Date & Time <span className="text-danger">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        className="form-control form-control-sm"
                        value={rescheduleDatetime}
                        onChange={(e) => setRescheduleDatetime(e.target.value)}
                        required
                      />
                      <small className="text-muted" style={{ fontSize: "11px" }}>
                        Call will automatically trigger within calling hours (09:00 AM - 07:30 PM).
                      </small>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-default btn-sm"
                      onClick={() => setIsRescheduleModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm"
                      disabled={rescheduleLoading}
                    >
                      {rescheduleLoading ? "Saving..." : "Save New Time"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}


      {/* Instant Single Call Modal */}
      {isInstantCallModalOpen && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
            onClick={() => setIsInstantCallModalOpen(false)}
          >
            <div className="modal-dialog modal-md" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h5 className="modal-title font-weight-bold">Dial Instant AI Call</h5>
                  <button
                    type="button"
                    className="btn btn-default float-end"
                    onClick={() => setIsInstantCallModalOpen(false)}
                  >
                    &times;
                  </button>
                </div>

                <form onSubmit={handleTriggerInstantCall}>
                  <div className="modal-body">
                    <div className="form-group mb-3">
                      <label className="control-label font-weight-bold">
                        Mobile Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        required
                        placeholder="10-digit mobile (e.g. 9876543210)"
                        value={instantCallForm.mobile}
                        onChange={(e) =>
                          setInstantCallForm({ ...instantCallForm, mobile: e.target.value })
                        }
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label className="control-label">Customer / Lead Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Ramesh Sharma"
                        value={instantCallForm.name}
                        onChange={(e) =>
                          setInstantCallForm({ ...instantCallForm, name: e.target.value })
                        }
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label className="control-label">Lead Status</label>
                      <select
                        className="form-control"
                        value={instantCallForm.leadStatus}
                        onChange={(e) =>
                          setInstantCallForm({ ...instantCallForm, leadStatus: e.target.value })
                        }
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="interested">Interested</option>
                        <option value="follow_up">Follow Up</option>
                        <option value="not_interested">Not Interested</option>
                      </select>
                    </div>

                    <div className="form-group mb-3">
                      <label className="control-label">CRM Sub UID (Optional)</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. 1042"
                        value={instantCallForm.subUid}
                        onChange={(e) =>
                          setInstantCallForm({ ...instantCallForm, subUid: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={() => setIsInstantCallModalOpen(false)}
                      disabled={instantCallLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={instantCallLoading}
                    >
                      <FontAwesomeIcon icon={faPhone} />{" "}
                      {instantCallLoading ? "Dialing..." : "Dial Call Now"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add / Sync Customer Modal */}
      {isAddCustomerModalOpen && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
            onClick={() => setIsAddCustomerModalOpen(false)}
          >
            <div className="modal-dialog modal-md" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h5 className="modal-title font-weight-bold">Add Customer to Voice Server</h5>
                  <button
                    type="button"
                    className="btn btn-default float-end"
                    onClick={() => setIsAddCustomerModalOpen(false)}
                  >
                    &times;
                  </button>
                </div>

                <form onSubmit={handleSaveCustomer}>
                  <div className="modal-body">
                    <div className="form-group mb-3">
                      <label className="control-label font-weight-bold">
                        Mobile Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        required
                        placeholder="10-digit mobile number"
                        value={customerForm.mobile}
                        onChange={(e) =>
                          setCustomerForm({ ...customerForm, mobile: e.target.value })
                        }
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label className="control-label font-weight-bold">
                        Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        required
                        placeholder="Customer name"
                        value={customerForm.name}
                        onChange={(e) =>
                          setCustomerForm({ ...customerForm, name: e.target.value })
                        }
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label className="control-label">Lead Status</label>
                      <select
                        className="form-control"
                        value={customerForm.lead_status}
                        onChange={(e) =>
                          setCustomerForm({ ...customerForm, lead_status: e.target.value })
                        }
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="interested">Interested</option>
                        <option value="follow_up">Follow Up</option>
                        <option value="not_interested">Not Interested</option>
                      </select>
                    </div>

                    <div className="form-group mb-3">
                      <label className="control-label">Details / Notes</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Notes or details"
                        value={customerForm.details}
                        onChange={(e) =>
                          setCustomerForm({ ...customerForm, details: e.target.value })
                        }
                      />
                    </div>

                    <div className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="dndCheckbox"
                        checked={customerForm.dnd}
                        onChange={(e) =>
                          setCustomerForm({ ...customerForm, dnd: e.target.checked })
                        }
                      />
                      <label className="form-check-label ms-2" htmlFor="dndCheckbox">
                        Do Not Call (DND) - Exclude from campaigns
                      </label>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={() => setIsAddCustomerModalOpen(false)}
                      disabled={customerFormLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={customerFormLoading}
                    >
                      {customerFormLoading ? "Saving..." : "Save Customer"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Transcript Detail Modal */}
      {selectedCallForDetails && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
            onClick={() => setSelectedCallForDetails(null)}
          >
            <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h5 className="modal-title font-weight-bold">Conversation Transcript</h5>
                  <button
                    type="button"
                    className="btn btn-default float-end"
                    onClick={() => setSelectedCallForDetails(null)}
                  >
                    &times;
                  </button>
                </div>
                <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                  <div className="bg-light p-3 border rounded">
                    {Array.isArray(selectedCallForDetails.transcript) ? (
                      selectedCallForDetails.transcript.map((t, idx) => (
                        <div key={idx} className="mb-2">
                          <strong className={t.role === "agent" ? "text-primary" : "text-success"}>
                            {t.role === "agent" ? "AI Agent: " : "Customer: "}
                          </strong>
                          <span>{t.text}</span>
                        </div>
                      ))
                    ) : (
                      <pre className="m-0">{JSON.stringify(selectedCallForDetails.transcript, null, 2)}</pre>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={() => setSelectedCallForDetails(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Custom Fields Modal (Tag 2) */}
      {isFieldsModalOpen && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
            onClick={() => setIsFieldsModalOpen(false)}
          >
            <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="modal-title font-weight-bold d-inline-block">Voice Server Custom Fields</h5>
                    <span className="text-muted ms-2" style={{ fontSize: "0.85rem" }}>
                      (AI Voice Agent Variables)
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-default float-end"
                    onClick={() => setIsFieldsModalOpen(false)}
                  >
                    &times;
                  </button>
                </div>

                <div className="modal-body">
                  {/* Create New Field Form */}
                  <div className="panel panel-default p-3 bg-light border mb-4">
                    <h6 className="font-weight-bold mb-3">Add New Custom Field</h6>
                    <form onSubmit={handleCreateField}>
                      <div className="row">
                        <div className="col-md-3 form-group mb-2">
                          <label className="control-label small font-weight-bold">Entity</label>
                          <select
                            className="form-control form-control-sm"
                            value={newFieldForm.entity}
                            onChange={(e) => setNewFieldForm({ ...newFieldForm, entity: e.target.value })}
                          >
                            <option value="customer">Customer</option>
                            <option value="call">Call</option>
                          </select>
                        </div>

                        <div className="col-md-3 form-group mb-2">
                          <label className="control-label small font-weight-bold">Field Key *</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="e.g. kyc_status"
                            required
                            value={newFieldForm.key}
                            onChange={(e) => setNewFieldForm({ ...newFieldForm, key: e.target.value })}
                          />
                        </div>

                        <div className="col-md-3 form-group mb-2">
                          <label className="control-label small font-weight-bold">Display Label *</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="e.g. KYC Status"
                            required
                            value={newFieldForm.label}
                            onChange={(e) => setNewFieldForm({ ...newFieldForm, label: e.target.value })}
                          />
                        </div>

                        <div className="col-md-3 form-group mb-2">
                          <label className="control-label small font-weight-bold">Data Type</label>
                          <select
                            className="form-control form-control-sm"
                            value={newFieldForm.type}
                            onChange={(e) => setNewFieldForm({ ...newFieldForm, type: e.target.value })}
                          >
                            <option value="string">Text / String</option>
                            <option value="number">Number</option>
                            <option value="boolean">Boolean (Yes/No)</option>
                          </select>
                        </div>
                      </div>

                      <div className="text-end mt-2">
                        <button
                          type="submit"
                          className="btn btn-primary btn-sm"
                          disabled={createFieldLoading}
                        >
                          {createFieldLoading ? "Creating Field..." : "+ Create Custom Field"}
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Existing Fields List */}
                  <h6 className="font-weight-bold mb-2">Configured Custom Fields</h6>
                  {fieldsLoading ? (
                    <div className="text-center p-3 text-muted">Loading fields...</div>
                  ) : customFields.length === 0 ? (
                    <div className="alert alert-info py-2 small mb-0">
                      No custom fields configured yet on Voice Server. You can create one above.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover table-condensed bg-white m-b-0 ai-voice-table sticky-table">
                        <thead>
                          <tr className="bg-light">
                            <th>Key</th>
                            <th>Label</th>
                            <th>Entity</th>
                            <th>Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customFields.map((f, idx) => (
                            <tr key={idx}>
                              <td><code>{f.key || f.name}</code></td>
                              <td className="font-weight-bold">{f.label || f.name}</td>
                              <td>
                                <span className="badge badge-secondary text-capitalize" style={{ padding: "3px 6px" }}>
                                  {f.entity || "customer"}
                                </span>
                              </td>
                              <td>
                                <span className="badge badge-info" style={{ padding: "3px 6px" }}>
                                  {f.type || "string"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={() => setIsFieldsModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit Campaign Modal */}
      {isEditCampaignModalOpen && editingCampaign && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1040 }}
            onClick={() => setIsEditCampaignModalOpen(false)}
          ></div>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ zIndex: 1050 }}
          >
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h5 className="modal-title font-weight-bold mb-0">
                    Edit Campaign: {editingCampaign.campaignName}
                  </h5>
                  <button
                    type="button"
                    className="btn btn-default float-end"
                    onClick={() => setIsEditCampaignModalOpen(false)}
                    style={{ fontSize: "16px", lineHeight: "1", padding: "4px 10px" }}
                  >
                    &times;
                  </button>
                </div>
                <form onSubmit={handleSaveEditCampaign}>
                  <div className="modal-body">
                    <p className="text-muted small">
                      Edit campaign parameters (allowed calling window and dialing speed).
                    </p>
                    <div className="form-group mb-3">
                      <label className="font-weight-bold">Allowed Start Time (HH:MM)</label>
                      <input
                        type="time"
                        className="form-control"
                        value={editCampaignForm.allowed_start_time}
                        onChange={(e) =>
                          setEditCampaignForm({
                            ...editCampaignForm,
                            allowed_start_time: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                    <div className="form-group mb-3">
                      <label className="font-weight-bold">Allowed End Time (HH:MM)</label>
                      <input
                        type="time"
                        className="form-control"
                        value={editCampaignForm.allowed_end_time}
                        onChange={(e) =>
                          setEditCampaignForm({
                            ...editCampaignForm,
                            allowed_end_time: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                    <div className="form-group mb-3">
                      <label className="font-weight-bold">Attempts Per Second</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0.1"
                        max="50"
                        className="form-control"
                        value={editCampaignForm.attempts_per_second}
                        onChange={(e) =>
                          setEditCampaignForm({
                            ...editCampaignForm,
                            attempts_per_second: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={() => setIsEditCampaignModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={editCampaignLoading}
                    >
                      {editCampaignLoading ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add Contacts to Campaign Modal */}
      {isAddContactsModalOpen && activeCampaignForContacts && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1040 }}
            onClick={() => setIsAddContactsModalOpen(false)}
          ></div>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ zIndex: 1050 }}
          >
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h5 className="modal-title font-weight-bold mb-0">
                    Add Contacts: {activeCampaignForContacts.campaignName}
                  </h5>
                  <button
                    type="button"
                    className="btn btn-default float-end"
                    onClick={() => setIsAddContactsModalOpen(false)}
                    style={{ fontSize: "16px", lineHeight: "1", padding: "4px 10px" }}
                  >
                    &times;
                  </button>
                </div>
                <form onSubmit={handleSaveAddContacts}>
                  <div className="modal-body">
                    <p className="text-muted small">
                      Add additional phone numbers to this active/scheduled campaign. In REAL mode, these new contacts will be queued and dialed.
                    </p>
                    <div className="form-group mb-3">
                      <label className="font-weight-bold">Mobile Numbers</label>
                      <textarea
                        className="form-control"
                        rows="5"
                        placeholder="Enter phone numbers separated by comma or new line (e.g. 9876543210, 9123456780)..."
                        value={addContactsMobiles}
                        onChange={(e) => setAddContactsMobiles(e.target.value)}
                        required
                      />
                      <span className="text-muted small">
                        Enter 10-digit mobile numbers. E.164 (+91) format will be applied automatically.
                      </span>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={() => setIsAddContactsModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={addContactsLoading}
                    >
                      {addContactsLoading ? "Adding Contacts..." : "+ Add Contacts"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Launch Campaign Modal */}
      {isLaunchModalOpen && (
        <AICampaignModal
          isOpen={isLaunchModalOpen}
          onClose={() => setIsLaunchModalOpen(false)}
          selectedLeads={[]}
          selectedCount={0}
          onSuccess={() => {
            fetchCampaigns();
          }}
        />
      )}

      {/* Right Slide-in Campaign Details Drawer */}
      {/* Backdrop */}
      {isCampaignDetailsModalOpen && (
        <div
          onClick={() => setIsCampaignDetailsModalOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.35)",
            zIndex: 1040,
          }}
        />
      )}

      {/* Drawer Panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: "520px",
          maxWidth: "95vw",
          backgroundColor: "#fff",
          zIndex: 1050,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-4px 0 20px rgba(0,0,0,0.18)",
          transform: isCampaignDetailsModalOpen ? "translateX(0)" : "translateX(110%)",
          transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {isCampaignDetailsModalOpen && selectedCampaignDetails && (() => {
          const counts = selectedCampaignDetails.counts || {};
          const reached = Number(counts.reached ?? selectedCampaignDetails.reachedCount ?? 0);
          const retry = Number(counts.retry ?? selectedCampaignDetails.retryCount ?? 0);
          const skipped = Number(counts.skipped ?? selectedCampaignDetails.skippedCount ?? 0);
          const total = Number(counts.total ?? selectedCampaignDetails.totalLeads ?? 0);
          const queued = Number(counts.queued ?? 0);
          const calling = Number(counts.calling ?? 0);
          const isFinished = Boolean(selectedCampaignDetails.calling_finished || selectedCampaignDetails.callingFinished);
          const failed = isFinished
            ? Math.max(0, total - reached - skipped)
            : Number(counts.failed ?? selectedCampaignDetails.failedCount ?? 0);
          const unreached = Number(counts.unreached ?? selectedCampaignDetails.unreachedCount ?? failed);
          const callsMade = Number(counts.calls_made ?? 0);
          const talkSeconds = Number(counts.talk_seconds ?? 0);

          const reachedPct = total > 0 ? (reached / total) * 100 : 0;
          const retryPct = total > 0 ? (retry / total) * 100 : 0;
          const failedPct = total > 0 ? (failed / total) * 100 : 0;
          const talkTimeDisplay = talkSeconds > 60
            ? `${Math.floor(talkSeconds / 60)}m ${talkSeconds % 60}s`
            : `${talkSeconds}s`;
          const campaignSettings = selectedCampaignDetails.settings || {};
          const allowedSchedule = campaignSettings.allowed_schedule || {};
          const cohorts = Array.isArray(selectedCampaignDetails.cohorts) ? selectedCampaignDetails.cohorts : [];
          const allowedDays = Array.isArray(allowedSchedule.allowed_days)
            ? allowedSchedule.allowed_days
            : Array.isArray(campaignSettings.allowed_days)
              ? campaignSettings.allowed_days
              : null;

          return (
            <>
              {/* Drawer Header */}
              <div className="modal-header d-flex justify-content-between align-items-start bg-light" style={{ flexShrink: 0, borderBottom: "1px solid #dee2e6", padding: "16px 20px" }}>
                <div style={{ flex: 1, paddingRight: "12px" }}>
                  <h5 className="modal-title font-weight-bold mb-1" style={{ fontSize: "15px" }}>
                    {selectedCampaignDetails.campaignName || "Campaign Details"}
                  </h5>
                  <div className="d-flex align-items-center flex-wrap" style={{ gap: "6px" }}>
                    {isFinished ? (
                      <span className="badge text-white" style={{ padding: "3px 8px", backgroundColor: "#10b981" }}>
                        <FontAwesomeIcon icon={faCheck} className="me-1" /> All calls done
                      </span>
                    ) : (
                      <span className="badge badge-info text-capitalize" style={{ padding: "3px 8px" }}>
                        {selectedCampaignDetails.server_status || selectedCampaignDetails.status}
                      </span>
                    )}
                    {selectedCampaignDetails.agentName && (
                      <span className="badge badge-secondary" style={{ padding: "3px 8px" }}>
                        Run by {selectedCampaignDetails.agentName}
                      </span>
                    )}
                    {(() => {
                      const startedDate =
                        selectedCampaignDetails.started_at ||
                        selectedCampaignDetails.startedAt ||
                        selectedCampaignDetails.settings?.start_timestamp ||
                        selectedCampaignDetails.createdOn;
                      return startedDate ? (
                        <span className="text-muted small">
                          Started {new Date(startedDate).toLocaleString()}
                        </span>
                      ) : null;
                    })()}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() => setIsCampaignDetailsModalOpen(false)}
                  style={{ fontSize: "18px", lineHeight: 1, padding: "2px 8px", flexShrink: 0 }}
                  title="Close"
                >
                  &times;
                </button>
              </div>

              {/* Drawer Body */}
              <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
                {campaignDetailsLoading && (
                  <div className="text-center text-muted small p-3">
                    Loading latest data...
                  </div>
                )}

                {/* Multi-color Segmented Progress Bar */}
                <div className="mb-3">
                  <div
                    style={{
                      width: "100%",
                      height: "10px",
                      backgroundColor: "#e2e8f0",
                      borderRadius: "9999px",
                      overflow: "hidden",
                      display: "flex",
                    }}
                  >
                    {reachedPct > 0 && (
                      <div
                        style={{
                          width: `${reachedPct}%`,
                          height: "100%",
                          backgroundColor: "#10b981",
                          transition: "width 0.4s ease",
                        }}
                      />
                    )}
                    {retryPct > 0 && (
                      <div
                        style={{
                          width: `${retryPct}%`,
                          height: "100%",
                          backgroundColor: "#f59e0b",
                          transition: "width 0.4s ease",
                        }}
                      />
                    )}
                    {failedPct > 0 && (
                      <div
                        style={{
                          width: `${failedPct}%`,
                          height: "100%",
                          backgroundColor: "#ef4444",
                          transition: "width 0.4s ease",
                        }}
                      />
                    )}
                  </div>
                  <div className="d-flex align-items-center flex-wrap mt-2" style={{ gap: "12px", fontSize: "12px" }}>
                    {reached > 0 && (
                      <span style={{ color: "#10b981", fontWeight: "600" }}>■ {reached} reached</span>
                    )}
                    {retry > 0 && (
                      <span style={{ color: "#d97706", fontWeight: "600" }}>■ {retry} will retry</span>
                    )}
                    {failed > 0 && (
                      <span style={{ color: "#ef4444", fontWeight: "600" }}>
                        ■ {failed} {isFinished ? "unreached" : "failed"}
                      </span>
                    )}
                    {reached === 0 && retry === 0 && failed === 0 && (
                      <span className="text-muted" style={{ fontSize: "11.5px" }}>■ {total} queued</span>
                    )}
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="row g-2 mb-3">
                  {[
                    { label: "Customers", value: total, cls: "" },
                    { label: "Calls made", value: callsMade, cls: "text-primary" },
                    { label: "Reached", value: `${reached}${total > 0 ? ` (${Math.round((reached / total) * 100)}%)` : ""}`, cls: "text-success" },
                    { label: "Talk time", value: talkTimeDisplay, cls: "text-info" },
                    (queued > 0 || calling > 0) ? { label: "Queued / Calling", value: `${queued} / ${calling}`, cls: "text-muted" } : null,
                    retry > 0 ? { label: "Will retry", value: retry, cls: "text-warning" } : null,
                    skipped > 0 ? { label: "Skipped", value: skipped, cls: "text-muted" } : null,
                    failed > 0 ? { label: isFinished ? "Unreached" : "Failed", value: failed, cls: "text-danger" } : null,
                  ].filter(Boolean).map((stat, i) => (
                    <div key={i} className="col-6 mb-2">
                      <div className="p-2 border rounded bg-light">
                        <small className="text-muted d-block">{stat.label}</small>
                        <strong className={`h6 mb-0 ${stat.cls}`}>{stat.value}</strong>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Outcomes */}
                {selectedCampaignDetails.outcomes && Object.keys(selectedCampaignDetails.outcomes).length > 0 && (
                  <div className="mb-3">
                    <h6 className="font-weight-bold mb-2" style={{ fontSize: "13px" }}>Outcomes of reached customers</h6>
                    <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                      {Object.entries(selectedCampaignDetails.outcomes).map(([key, count]) => (
                        <span key={key} className="badge badge-light border text-dark" style={{ fontSize: "12px", padding: "5px 10px", fontWeight: 500 }}>
                          {key.replace(/_/g, " ")}: <strong className="text-primary">{String(count)}</strong>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contacts Table */}
                {Array.isArray(selectedCampaignDetails.contacts) && selectedCampaignDetails.contacts.length > 0 && (
                  <div className="mb-3">
                    <h6 className="font-weight-bold mb-2" style={{ fontSize: "13px" }}>Customers</h6>
                    <div className="table-responsive" style={{ border: "1px solid #dee2e6", borderRadius: "4px" }}>
                      <table className="table table-hover table-condensed table-sm mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>Customer</th>
                            <th>Mobile</th>
                            <th>Progress</th>
                            <th>Tries</th>
                            <th>Last call</th>
                            <th>Outcome</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedCampaignDetails.contacts.map((contact, idx) => (
                            <tr key={idx}>
                              <td className="font-weight-bold">{contact.name || contact.customer_name || "—"}</td>
                              <td className="text-muted">{contact.mobile || contact.phone || "—"}</td>
                              <td>
                                {contact.state === "reached" || contact.result === "reached" ? (
                                  <span className="text-success font-weight-bold">Reached</span>
                                ) : contact.state === "retry" || contact.result === "retry" || (contact.attempts > 0 && (contact.call_status === "busy" || contact.result === "busy") && !isFinished) ? (
                                  <span style={{ color: "#d97706", fontWeight: "600" }}>Will retry</span>
                                ) : (
                                  <span className="text-muted text-capitalize">{contact.state || contact.result || "—"}</span>
                                )}
                              </td>
                              <td>{contact.attempts ?? 1}</td>
                              <td>
                                {contact.call_status ? (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                                    <span
                                      style={{
                                        width: "6px",
                                        height: "6px",
                                        borderRadius: "50%",
                                        backgroundColor:
                                          contact.call_status === "completed" || contact.call_status === "answered"
                                            ? "#10b981"
                                            : contact.call_status === "busy" || contact.call_status === "failed"
                                            ? "#ef4444"
                                            : "#f59e0b",
                                      }}
                                    />
                                    <span
                                      style={{
                                        color:
                                          contact.call_status === "completed" || contact.call_status === "answered"
                                            ? "#10b981"
                                            : contact.call_status === "busy" || contact.call_status === "failed"
                                            ? "#ef4444"
                                            : "#d97706",
                                        fontWeight: "600",
                                        textTransform: "capitalize",
                                      }}
                                    >
                                      {contact.call_status}
                                    </span>
                                  </span>
                                ) : "—"}
                              </td>
                              <td className="text-muted" style={{ maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                <span title={contact.outcome || ""}>{contact.outcome || "—"}</span>
                                {contact.summary && (
                                  <small
                                    className="d-block text-muted"
                                    title={contact.summary}
                                    style={{ maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis" }}
                                  >
                                    {contact.summary}
                                  </small>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {cohorts.length > 0 && (
                  <div className="mb-3">
                    <h6 className="font-weight-bold mb-2" style={{ fontSize: "13px" }}>Cohorts sent</h6>
                    <div className="table-responsive" style={{ border: "1px solid #dee2e6", borderRadius: "4px" }}>
                      <table className="table table-hover table-condensed table-sm mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>Cohort</th>
                            <th>Status</th>
                            <th>Sent</th>
                            <th>Valid</th>
                            <th>Rejected</th>
                            <th>Added</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cohorts.map((cohort, idx) => (
                            <tr key={cohort.id || cohort.name || idx}>
                              <td>{cohort.cohort_name || cohort.name || "—"}</td>
                              <td className="text-capitalize">{cohort.status || "—"}</td>
                              <td>{cohort.sent ?? "—"}</td>
                              <td>{cohort.valid ?? "—"}</td>
                              <td>{cohort.rejected ?? "—"}</td>
                              <td>{cohort.added_at ? new Date(cohort.added_at).toLocaleString() : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Settings Info */}
                {(selectedCampaignDetails.settings || selectedCampaignDetails.voiceServerCampaignId) && (
                  <div className="mb-3">
                    <h6 className="font-weight-bold mb-2" style={{ fontSize: "13px" }}>Settings</h6>
                    <div className="row g-2">
                      {[
                        selectedCampaignDetails.agentName && { label: "Agent", value: selectedCampaignDetails.agentName },
                        (campaignSettings.dials_from || campaignSettings.dial_from || campaignSettings.phone_number || campaignSettings.caller_id) && {
                          label: "Dials from",
                          value: campaignSettings.dials_from || campaignSettings.dial_from || campaignSettings.phone_number || campaignSettings.caller_id,
                        },
                        (selectedCampaignDetails.started_at || selectedCampaignDetails.startedAt || campaignSettings.start_timestamp || selectedCampaignDetails.scheduleAt) && {
                          label: "Period starts",
                          value: new Date(selectedCampaignDetails.started_at || selectedCampaignDetails.startedAt || campaignSettings.start_timestamp || selectedCampaignDetails.scheduleAt).toLocaleString(),
                        },
                        selectedCampaignDetails.voiceServerCampaignId && { label: "Sarvam Campaign ID", value: selectedCampaignDetails.voiceServerCampaignId },
                        campaignSettings.attempts_per_second && { label: "Attempts / sec", value: campaignSettings.attempts_per_second },
                        campaignSettings.max_retries && { label: "Retries", value: `up to ${campaignSettings.max_retries}` },
                        (campaignSettings.allowed_start_time || allowedSchedule.allowed_start_time) && {
                          label: "Calls allowed",
                          value: `${campaignSettings.allowed_start_time || allowedSchedule.allowed_start_time}–${campaignSettings.allowed_end_time || allowedSchedule.allowed_end_time || ""}`,
                        },
                        allowedDays && {
                          label: "Allowed days",
                          value: allowedDays.join(", "),
                        },
                        selectedCampaignDetails.segment && { label: "Segment", value: selectedCampaignDetails.segment },
                      ].filter(Boolean).map((s, i) => (
                        <div key={i} className="col-6 mb-2">
                          <div className="p-2 border rounded bg-light">
                            <small className="text-muted d-block">{s.label}</small>
                            <strong className="small" style={{ wordBreak: "break-all" }}>{String(s.value)}</strong>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Drawer Footer */}
              <div className="modal-footer bg-light d-flex align-items-center" style={{ flexShrink: 0, gap: "8px", justifyContent: "flex-start", borderTop: "1px solid #dee2e6", padding: "12px 20px" }}>
                <button
                  type="button"
                  className="btn btn-default btn-sm"
                  onClick={() => fetchCampaignDetail(selectedCampaignDetails.id, true)}
                  disabled={campaignDetailsLoading}
                  title="Refresh Live Status"
                >
                  <FontAwesomeIcon icon={faRefresh} spin={campaignDetailsLoading} /> Sync now
                </button>

                {isFinished ? (
                  <>
                    {selectedCampaignDetails.status !== "ended" && selectedCampaignDetails.status !== "canceled" && (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleControlCampaign(selectedCampaignDetails.id, "stop")}
                      >
                        <FontAwesomeIcon icon={faStop} className="me-1" /> Close
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    {(selectedCampaignDetails.status === "running" || selectedCampaignDetails.server_status === "active") && (
                      <button
                        type="button"
                        className="btn btn-warning btn-sm"
                        onClick={() => handleControlCampaign(selectedCampaignDetails.id, "pause")}
                      >
                        <FontAwesomeIcon icon={faPause} className="me-1" /> Pause
                      </button>
                    )}
                    {(selectedCampaignDetails.status === "paused" || selectedCampaignDetails.server_status === "paused") && (
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => handleControlCampaign(selectedCampaignDetails.id, "resume")}
                      >
                        <FontAwesomeIcon icon={faPlay} className="me-1" /> Resume
                      </button>
                    )}
                    {selectedCampaignDetails.status !== "ended" && selectedCampaignDetails.status !== "canceled" && (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleControlCampaign(selectedCampaignDetails.id, "stop")}
                      >
                        <FontAwesomeIcon icon={faStop} className="me-1" /> Stop
                      </button>
                    )}
                  </>
                )}
              </div>
            </>
          );
        })()}
      </div>

      {/* Audio Player Popup */}
      <AudioPlayerPopup
        isOpen={Boolean(popupAudio)}
        onClose={() => setPopupAudio(null)}
        name={popupAudio?.name}
        audioUrl={popupAudio?.audioUrl}
      />
    </div>
  );
};

export default AICallDashboard;
